import {
    AfterContentChecked,
    AfterContentInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    QueryList
} from "@angular/core";
import {PageChangeEvent} from "@progress/kendo-angular-grid";
import {SortDescriptor} from "@progress/kendo-data-query";
import {Subscription} from "rxjs";
import {ConfigurationService, SearchModel} from "@fedeltapointofsale/data-client";
import {
    FUIBreakpoints,
    FUIFormContext,
    FUIInlineFormContext,
    FUIResponsiveService,
    FUIStackedPageContextService
} from "@fedeltapointofsale/ui";
import {ColumnComponent} from "../../grid/column.component";
import {SearchActionEvent} from "../search-page/search-action-event";
import {SearchType} from "../search-page/extended-search-params";
import {SearchPageService} from "../search-page/search-page.service";
import {AbstractColumnComponent, ActionEvent} from "../../grid/abstract-column.component";
import {AbstractGridComponent} from "../../grid/abstract-grid.component";
import {TranslateService} from "@ngx-translate/core";
import {StringUtils} from "../../../utils/string-utils";

/**
 * Pagination event
 */
export class PageGridChangeEvent {
    constructor(public start: number, public limit: number) {
    }
}

/**
 * Sort field descriptor
 */
export class SortField {
    field: string;
    dir: string;

    constructor(field: string, dir: string) {
        this.field = field;
        this.dir = dir;
    }
}

/**
 * Sort event
 */
export class SortGridChangeEvent {
    sortFields: SortField[];

    constructor(sortFields: SortField[]) {
        this.sortFields = sortFields;
    }
}

/**
 * Grid component to be used in a search. It listen to events from every SearchInputComponent and trigger its own events.
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "wbo-search-grid",
    styleUrls: ["./search-grid.component.scss"],
    templateUrl: "./search-grid.component.html",
    providers: [{provide: FUIFormContext, useClass: FUIInlineFormContext}]
})
export class SearchGridComponent extends AbstractGridComponent implements OnInit, AfterContentInit, AfterContentChecked, OnDestroy {
    StringUtils = StringUtils;

    @Input() public styleOddRows: boolean = true;
    @Input() public disableViewAction: boolean = false;
    @HostBinding("style.width.px") boundary: number;
    public buttonCount: number = 5;
    public pageSizes: Array<number>;
    public previousNext: boolean = true;
    public info: boolean = true;
    public type: "numeric" | "input" = "numeric";
    @Output() public itemAction: EventEmitter<SearchActionEvent> = new EventEmitter<SearchActionEvent>();
    @Output() public pageChange: EventEmitter<PageGridChangeEvent> = new EventEmitter<PageGridChangeEvent>();
    @Output() public sortChange: EventEmitter<SortGridChangeEvent> = new EventEmitter<SortGridChangeEvent>();
    private itemActionSubscriptions: Subscription[] = [];
    private dataViewSubscription: Subscription;
    private selectedRecords: any[] = [];

    constructor(changeDetectorRef: ChangeDetectorRef,
                @Optional() private stackedPageContextService: FUIStackedPageContextService,
                public searchPageService: SearchPageService,
                public model: SearchModel<any>,
                translateService: TranslateService,
                configService: ConfigurationService,
                private responsiveService: FUIResponsiveService) {
        super(changeDetectorRef, translateService, configService);

        this.pageSizes = new Array<number>(25, 50, 100);

        if (stackedPageContextService) {
            this.stackedPageContextService.title = "wbo." + model.getResource() + ".search.title";
            this.stackedPageContextService.onCloseRequest.subscribe((closeConfirmation) => closeConfirmation.canClose = true);
        }

        this.dataViewSubscription = model.dataViewChange.subscribe(() => {
            this.allSelected = this.isAllSelected();
            this.changeDetectorRef.detectChanges();
        });

        this.subscriptions.add(this.responsiveService.windowWidth$.subscribe(
            () => {
                if (this.responsiveService.breakpointApplies(FUIBreakpoints.smallDown)) {
                    this.boundary = this.responsiveService.host().innerWidth - 32;
                } else {
                    this.boundary = null;
                }
            }
        ));

        this.sortable = true;
    }

    get isSelectMode(): boolean {
        return this.stackedPageContextService != undefined && this.stackedPageContextService.params.searchType !== SearchType.NO_SELECTION;
    }

    get isMultiSelectMode(): boolean {
        return this.stackedPageContextService.params.searchType === SearchType.MULTIPLE_SELECTION;
    }

    get isSingleSelectMode(): boolean {
        return this.stackedPageContextService.params.searchType === SearchType.SINGLE_SELECTION;
    }

    get showTotalPages(): boolean {
        return this.model.countable;
    }

    get disableNextButton(): boolean {
        return !this.model.countable && this.model.data.length < this.model.limit;
    }

    ngOnInit(): void {
        if (!this.persistenceId) {
            this.persistenceId = "search." + this.model.getResource();
        }
    }

    ngAfterContentInit(): void {
        this.evaluateHideableColumns();
        this.columns.changes.subscribe(() => {
            this.evaluateHideableColumns();
            this.changeDetectorRef.detectChanges();
        });
        this.columns.forEach((item) => {
            this.itemActionSubscriptions.push(item.itemAction.subscribe(event => this.handleActionEvent(event)))
        });
    }

    ngOnDestroy(): void {
        this.itemActionSubscriptions.forEach((subscription) => subscription.unsubscribe());

        this.dataViewSubscription.unsubscribe();

        this.subscriptions.unsubscribe();
    }

    /**
     * Handles an action event that is emitted from a column and passes it on to be subscribed by higher level
     * components
     * @param event
     */
    handleActionEvent(event: ActionEvent) {
        this.itemAction.emit(<SearchActionEvent> event);
    }

    onPageChange(event: PageChangeEvent): void {
        this.pageChange.emit(new PageGridChangeEvent(event.skip, event.take));
    }

    onSortChange(sort: SortDescriptor[]): void {
        const columns: QueryList<ColumnComponent> = this.columns;
        const sortFields: SortField[] = sort.filter(value => {
            const column: AbstractColumnComponent = columns.find(item => item.columnName === value.field);
            return column && (column.allowDescendingSorting || value.dir !== "desc");
        }).map(function (value) {
            return new SortField(value.field, value.dir);
        });
        this.sortChange.emit(new SortGridChangeEvent(sortFields));
    }

    toggleSelectAll(checked: boolean): void {
        if (this.allSelected != checked) {
            this.allSelected = checked;
            if (checked) {
                this.selectPage();
            } else {
                this.unselectPage();
            }
        }
    }

    isAllSelected(): boolean {
        let all: boolean = true;
        this.model.data.some((d: any) => {
            const id = this.model.getIdentifier(d);
            const index: number = this.selectedRecords.findIndex(record => this.model.getIdentifier(record) === id);
            if (index == -1) {
                all = false;
                return true;
            }
            return false;
        });

        return all;
    }

    selectRecord(checked: boolean, selectedRecord: any): void {
        const selectedIdentifier: any = this.model.getIdentifier(selectedRecord);
        const index: number = this.selectedRecords.findIndex(record => this.model.getIdentifier(record) === selectedIdentifier);
        if (checked) {
            if (index === -1) {
                this.selectedRecords.push(selectedRecord);
                this.allSelected = this.isAllSelected();
            }
        } else {
            if (index !== -1) {
                this.selectedRecords.splice(index, 1);
                this.allSelected = false;
            }
        }
        this.itemAction.emit(new SearchActionEvent(SearchActionEvent.ACTION_SELECT_RECORDS, this.selectedRecords));
        this.changeDetectorRef.markForCheck();
    }

    /**
     * Invoked when the select-single button is clicked
     * @param dataItem - row data
     */
    fireSelectSingleEvent(selectedRecord: any) {
        this.itemAction.emit(new SearchActionEvent(SearchActionEvent.ACTION_SELECT_SINGLE, selectedRecord));
    }

    viewRecord(selectedRecord: any): void {
        this.itemAction.emit(new SearchActionEvent(SearchActionEvent.ACTION_VIEW, selectedRecord));
    }

    isSelected(record: any): boolean {
        return this.selectedRecords.find(item => this.model.getIdentifier(item) === this.model.getIdentifier(record)) != undefined;
    }

    selectPage(): void {
        // this.checkBoxPageDropdown.show = false;
        this.model.data.forEach(item => this.selectRecord(true, item));
    }

    unselectPage(): void {
        this.model.data.forEach(item => this.selectRecord(false, item));
    }

    unSelectAll(): void {
        // this.checkBoxPageDropdown.show = false;
        this.selectedRecords = [];
        this.changeDetectorRef.detectChanges();
    }

    ngAfterContentChecked(): void {
        // when a there is a checkbox-control in the grid it is throwing an exception ExpressionChangedAfterItHasBeenCheckedError so
        // detecting the changes here is solving the issue
        this.changeDetectorRef.detectChanges();
    }

    override onHeaderTemplateClick(e): void {
        // make the sorting to work
        e.target.parentNode.parentNode.click()
    }
}
